import { useContext, useState, useEffect } from "react";
import { Globe, Link as LinkIcon, ExternalLink, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import MerchantContext from "../../contexts/MerchantContext";
import { trpc } from "../../trpc";

type PortalSchema = {
  source: string;
  string: string;
  regex: string;
};

type ListingUrlFormat = {
  type: "single" | "byListingType";
  single?: { format: string };
  byListingType?: {
    rentals?: string;
    sales?: string;
    mlsSalesCommercial?: string;
    mlsSalesResidential?: string;
  };
};

type PortalesConfigurationProps = {
  merchant: any;
};

const PortalesConfiguration = ({ merchant }: PortalesConfigurationProps): JSX.Element => {
  const { merchantId } = useContext(MerchantContext);
  const [isOpen, setIsOpen] = useState(false);

  const [metrocuadradoCode, setMetrocuadradoCode] = useState("");
  const [ciencuadrasCode, setCiencuadrasCode] = useState("");
  const [enableFincaraiz, setEnableFincaraiz] = useState(false);
  const [enableMercadolibre, setEnableMercadolibre] = useState(false);
  const [webUrl, setWebUrl] = useState("");
  const [listingUrlFormat, setListingUrlFormat] = useState<ListingUrlFormat>({
    type: "single",
    single: { format: "" },
    byListingType: { rentals: "", sales: "" },
  });

  useEffect(() => {
    // Reset state first
    setMetrocuadradoCode("");
    setCiencuadrasCode("");
    setEnableFincaraiz(false);
    setEnableMercadolibre(false);
    setWebUrl("");
    setListingUrlFormat({
      type: "single",
      single: { format: "" },
      byListingType: { rentals: "", sales: "" },
    });

    if (merchant) {
      // Load portal schemas
      if ((merchant as any).portalSchemas) {
        const schemas = (merchant as any).portalSchemas as PortalSchema[];

        schemas.forEach((schema) => {
          if (schema.source === "metrocuadrado") {
            setMetrocuadradoCode(schema.string);
          } else if (schema.source === "ciencuadras") {
            setCiencuadrasCode(schema.string);
          } else if (schema.source === "fincaraiz") {
            setEnableFincaraiz(true);
          } else if (schema.source === "mercadolibre") {
            setEnableMercadolibre(true);
          } else if (schema.source === "web") {
            setWebUrl(schema.string);
          }
        });
      }

      // Load listing URL format
      if ((merchant as any).listingUrlFormat) {
        const format = (merchant as any).listingUrlFormat as ListingUrlFormat;
        setListingUrlFormat(format);
      }
    }
  }, [merchant]);

  const utils = trpc.useUtils();
  const updateIntegrationConfig = trpc.merchants.updateIntegrationConfig.useMutation({
    onSuccess: async () => {
      await utils.merchants.get.invalidate({ id: merchantId });
      toast.success("Configuración guardada", {
        description: "Los portales han sido configurados correctamente",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo guardar la configuración",
      });
    },
  });

  const deleteIntegrationConfig = trpc.merchants.updateIntegrationConfig.useMutation({
    onSuccess: async () => {
      await utils.merchants.get.invalidate({ id: merchantId });
      setIsOpen(false);
      toast.success("Configuración eliminada", {
        description: "La configuración de portales ha sido eliminada correctamente",
      });
    },
    onError: (error) => {
      toast.error("Error", {
        description: error.message || "No se pudo eliminar la configuración",
      });
    },
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const portalSchemas: PortalSchema[] = [];

    // Add Metrocuadrado if code is provided
    if (metrocuadradoCode.trim()) {
      portalSchemas.push({
        source: "metrocuadrado",
        string: metrocuadradoCode.trim(),
        regex: `(${metrocuadradoCode.trim()}-[a-zA-Z0-9]+)`,
      });
    }

    // Add Ciencuadras if code is provided
    if (ciencuadrasCode.trim()) {
      portalSchemas.push({
        source: "ciencuadras",
        string: ciencuadrasCode.trim(),
        regex: `(${ciencuadrasCode.trim()}-\\d+)`,
      });
    }

    // Add Fincaraiz if enabled
    if (enableFincaraiz) {
      portalSchemas.push({
        source: "fincaraiz",
        string: "fincaraiz",
        regex: "(?:^|[^\\w.\\-])(\\d+)(?![\\w.\\-])",
      });
    }

    // Add Mercado Libre if enabled
    if (enableMercadolibre) {
      portalSchemas.push({
        source: "mercadolibre",
        string: "MCO",
        regex: "(MCO-\\d+)",
      });
    }

    // Add Web URL if provided
    if (webUrl.trim()) {
      portalSchemas.push({
        source: "web",
        string: webUrl.trim(),
        regex: "(?:\\d+-?)?\\d+(?!.*\\d)",
      });
    }

    // Build listing URL format payload — null signals removal so the backend
    // clears the field; undefined would be stripped by JSON and treated as "no change".
    let listingUrlFormatPayload: ListingUrlFormat | null = null;

    if (listingUrlFormat.type === "single" && listingUrlFormat.single?.format?.trim()) {
      listingUrlFormatPayload = {
        type: "single",
        single: { format: listingUrlFormat.single.format.trim() },
      };
    } else if (listingUrlFormat.type === "byListingType") {
      const rentals = listingUrlFormat.byListingType?.rentals?.trim();
      const sales = listingUrlFormat.byListingType?.sales?.trim();
      if (rentals || sales) {
        listingUrlFormatPayload = {
          type: "byListingType",
          byListingType: {
            ...(rentals ? { rentals } : {}),
            ...(sales ? { sales } : {}),
          },
        };
      }
    }

    updateIntegrationConfig.mutate({
      merchantId,
      portalSchemas,
      listingUrlFormat: listingUrlFormatPayload,
    });
  };

  const handleDelete = () => {
    deleteIntegrationConfig.mutate({
      merchantId,
      portalSchemas: [],
      listingUrlFormat: null,
    });
  };

  const hasAnyValue =
    metrocuadradoCode.trim() ||
    ciencuadrasCode.trim() ||
    enableFincaraiz ||
    enableMercadolibre ||
    webUrl.trim();

  const hasExistingConfig = merchant && (
    ((merchant as any).portalSchemas && (merchant as any).portalSchemas.length > 0) ||
    ((merchant as any).listingUrlFormat)
  );

  return (
    <div className="mx-auto max-w-[800px]">
      <div className="flex flex-col gap-6">
        <div>
          <h2 className="mb-2 text-2xl font-bold">
            Configuración de Portales e Integraciones
          </h2>
          <p className="text-gray-600">
            Configure los códigos de identificación de sus propiedades en diferentes portales
            inmobiliarios y el formato de las URLs de su sitio web.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6">
            {/* Portal Cards */}
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <div className="flex flex-col gap-6">
                <div className="flex items-center gap-2">
                  <LinkIcon className="size-5 text-blue-500" />
                  <h3 className="text-lg font-semibold">Portales Inmobiliarios</h3>
                </div>

                <div className="flex flex-col gap-4">
                  {/* Metrocuadrado */}
                  <div>
                    <Label className="font-semibold">Metro Cuadrado</Label>
                    <Input
                      placeholder="Ej: 5606"
                      value={metrocuadradoCode}
                      onChange={(e) => setMetrocuadradoCode(e.target.value)}
                      className="mt-1.5 bg-white"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Ingrese el código que antecede a sus listados en Metro Cuadrado
                    </p>
                  </div>

                  <Separator />

                  {/* Ciencuadras */}
                  <div>
                    <Label className="font-semibold">Ciencuadras</Label>
                    <Input
                      placeholder="Ej: 17035"
                      value={ciencuadrasCode}
                      onChange={(e) => setCiencuadrasCode(e.target.value)}
                      className="mt-1.5 bg-white"
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Ingrese el código que antecede a sus listados en Ciencuadras
                    </p>
                  </div>

                  <Separator />

                  {/* Fincaraiz */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={enableFincaraiz}
                      onCheckedChange={(checked) => setEnableFincaraiz(checked === true)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold">Finca Raíz</p>
                      <p className="text-sm text-gray-500">
                        Habilitar detección automática de códigos de Finca Raíz
                      </p>
                    </div>
                  </div>

                  <Separator />

                  {/* Mercado Libre */}
                  <div className="flex items-start gap-3">
                    <Checkbox
                      checked={enableMercadolibre}
                      onCheckedChange={(checked) => setEnableMercadolibre(checked === true)}
                      className="mt-1"
                    />
                    <div>
                      <p className="font-semibold">Mercado Libre</p>
                      <p className="text-sm text-gray-500">
                        Habilitar detección automática de códigos MCO de Mercado Libre
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Web URL Card */}
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <Globe className="size-5 text-green-500" />
                  <h3 className="text-lg font-semibold">Página Web</h3>
                </div>

                <div>
                  <Label className="font-semibold">URL de su sitio web</Label>
                  <Input
                    placeholder="Ej: conalturainmobiliaria.com"
                    value={webUrl}
                    onChange={(e) => setWebUrl(e.target.value)}
                    className="mt-1.5 bg-white"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Ingrese el dominio de su página web (sin https://)
                  </p>
                </div>
              </div>
            </div>

            {/* Listing URL Format Card */}
            <div className="rounded-lg border bg-card p-6 text-card-foreground shadow-sm">
              <div className="flex flex-col gap-4">
                <div className="flex items-center gap-2">
                  <ExternalLink className="size-5 text-purple-500" />
                  <h3 className="text-lg font-semibold">Formato para la URL del inmueble</h3>
                </div>

                <div>
                  <Label className="font-semibold">Tipo de formato</Label>
                  <select
                    value={listingUrlFormat.type}
                    onChange={(e) =>
                      setListingUrlFormat((prev) => ({
                        ...prev,
                        type: e.target.value as "single" | "byListingType",
                      }))
                    }
                    className="mt-1.5 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm hover:border-gray-300 focus:border-purple-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-purple-500"
                  >
                    <option value="single">Simple</option>
                    <option value="byListingType">Por tipo de listado</option>
                  </select>
                  <p className="mt-1 text-sm text-gray-500">
                    Configure cómo se generan los enlaces a las propiedades en su sitio web
                  </p>
                </div>

                {listingUrlFormat.type === "single" && (
                  <div>
                    <Label className="font-semibold">Formato URL</Label>
                    <Textarea
                      placeholder="Ej: https://miweb.com/inmueble/{code}"
                      value={listingUrlFormat.single?.format || ""}
                      onChange={(e) =>
                        setListingUrlFormat((prev) => ({
                          ...prev,
                          single: { format: e.target.value },
                        }))
                      }
                      className="mt-1.5 bg-white"
                      rows={2}
                    />
                    <div className="mt-1 text-sm text-gray-500">
                      <p>Marcadores disponibles:</p>
                      <ul className="ml-4 list-disc">
                        <li>{"{code}"} — código del inmueble (obligatorio)</li>
                        <li>{"{secondaryCode}"}, {"{internalCode}"}, {"{slug}"}</li>
                        <li>{"{propertyTypeRaw}"} — tipo de inmueble tal como viene del proveedor</li>
                        <li>
                          {"{propertyTypeRawSlug}"}, {"{neighborhoodSlug}"} — versión en minúsculas, sin tildes y con guiones (ej. &quot;Vereda San José&quot; → &quot;vereda-san-jose&quot;)
                        </li>
                      </ul>
                      <p className="mt-1">
                        Ejemplo: https://miweb.com/propiedades/{"{propertyTypeRawSlug}"}-{"{neighborhoodSlug}"}/893-{"{code}"}/
                      </p>
                    </div>
                  </div>
                )}

                {listingUrlFormat.type === "byListingType" && (
                  <div className="flex flex-col gap-3">
                    <div>
                      <Label className="font-semibold">Formato URL - Arriendo</Label>
                      <Textarea
                        placeholder="Ej: https://miweb.com/arriendo/{code}"
                        value={listingUrlFormat.byListingType?.rentals || ""}
                        onChange={(e) =>
                          setListingUrlFormat((prev) => ({
                            ...prev,
                            byListingType: {
                              ...(prev.byListingType || {}),
                              rentals: e.target.value,
                            },
                          }))
                        }
                        className="mt-1.5 bg-white"
                        rows={2}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        URL para propiedades en arriendo
                      </p>
                    </div>
                    <div>
                      <Label className="font-semibold">Formato URL - Venta</Label>
                      <Textarea
                        placeholder="Ej: https://miweb.com/venta/{code}"
                        value={listingUrlFormat.byListingType?.sales || ""}
                        onChange={(e) =>
                          setListingUrlFormat((prev) => ({
                            ...prev,
                            byListingType: {
                              ...(prev.byListingType || {}),
                              sales: e.target.value,
                            },
                          }))
                        }
                        className="mt-1.5 bg-white"
                        rows={2}
                      />
                      <p className="mt-1 text-sm text-gray-500">
                        URL para propiedades en venta
                      </p>
                    </div>
                    <div className="text-sm text-gray-500">
                      <p>Marcadores disponibles en cada formato:</p>
                      <ul className="ml-4 list-disc">
                        <li>{"{code}"} — código del inmueble (obligatorio)</li>
                        <li>{"{secondaryCode}"}, {"{internalCode}"}, {"{slug}"}</li>
                        <li>{"{propertyTypeRaw}"} — tipo de inmueble tal como viene del proveedor</li>
                        <li>
                          {"{propertyTypeRawSlug}"}, {"{neighborhoodSlug}"} — versión en minúsculas, sin tildes y con guiones (ej. &quot;Vereda San José&quot; → &quot;vereda-san-jose&quot;)
                        </li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              disabled={updateIntegrationConfig.isLoading || !hasAnyValue}
            >
              {updateIntegrationConfig.isLoading && <Loader2 className="size-4 animate-spin" />}
              Guardar Configuración
            </Button>

            {/* Delete Button */}
            {hasExistingConfig && (
              <Button
                variant="destructive"
                size="lg"
                onClick={() => setIsOpen(true)}
                disabled={updateIntegrationConfig.isLoading || deleteIntegrationConfig.isLoading}
                className="bg-transparent border border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
              >
                <Trash2 className="size-4" />
                Eliminar Configuración
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Delete Confirmation Modal */}
      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Eliminar Configuración
            </AlertDialogTitle>
            <AlertDialogDescription>
              ¿Está seguro de que desea eliminar toda la configuración de portales e integraciones?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
              disabled={deleteIntegrationConfig.isLoading}
            >
              {deleteIntegrationConfig.isLoading && <Loader2 className="size-4 animate-spin" />}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalesConfiguration;
